import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { ChallengesDataService, ChallengesColumnsService } from '../../services';
import { DialogChallengeFormComponent } from './challenge-form/challenge-form.component';
import { DialogChallengeDetailsComponent } from './challenge-details/challenge-details.component';
import { Challenge, ChallengesResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-challenges',
  templateUrl: './challenges.component.html',
  styleUrls: ['./challenges.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class ChallengesComponent implements OnInit, OnDestroy {

  private challengeAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private challengeEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private challengeDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public challengeToDelete: Challenge = {};
  public challenges: Challenge[] = [];
  public challenge: Challenge = {};
  public selectedChallenges: Challenge[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private challengesDataService: ChallengesDataService,
    private challengesColumnsService: ChallengesColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('ChallengesComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Challenges - Management');
    this.primengConfig.ripple = true;
    this.getChallenges();
  }

  ngOnDestroy(): void {
    console.log('ChallengesComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getChallenges(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.challengesDataService.getChallenges(this.parameters).subscribe((response: ChallengesResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.challenges = response.results;
        } else {
          this.challenges = this.challenges.concat(response.results);
        }
        if (response.inlineCount > this.challenges.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getChallenges();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('POSTSNOTLOADED')});
    });
  }

  importChallenges(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.challengesDataService.addChallenges(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('POSTSCREATED')});
                if (proceed === j) {
                  this.getChallenges();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('POSTSNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('POSTSNOTCREATED') + ' ' + msg});
            });
          } else {
            this.challengesDataService.updateChallenges(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('POSTSUPDATED')});
                if (proceed === j) {
                  this.getChallenges();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('POSTSNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('POSTSNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.challengesColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('challenges.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedChallenges() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Challenges?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.challengesDataService.deleteChallenges(this.selectedChallenges).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Challenges Deleted', life: 3000});
            this.getChallenges();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Challenges not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Challenges not deleted'});
        });
        this.selectedChallenges = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.challenges, this.columns, 'Challenges');
  }

  exportExcel() {
    ToolsService.exportExcel(this.challenges, this.columns, 'Challenges');
  }

  exportCsv() {
    ToolsService.exportCsv(this.challenges, this.columns, 'Challenges');
  }

  refresh() {
    this.challengesDataService.clearCache();
    this.getChallenges();
  }

  challengeAdd(): void {
    if (!this.navigation.getParamValue('challenges.addInDialog', true)) {
      this.router.navigate(['/challenges/add']);
      return;
    }
    this.challengeAddDialog = this.dialogService.open(DialogChallengeFormComponent, {
      header: '', // this.translate.instant('NEWPOST'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        challenges: {id: null},
        users: this.challengesColumnsService.users,
      },
    });

    this.challengeAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'Challenge added'});
        this.getChallenges();
      }
    });
  }

  challengeEdit(Challenge: Challenge): void {
    if (!this.navigation.getParamValue('Challenges.editInDialog', true)) {
      this.router.navigate(['/Challenges/edit/' + Challenge.id]);
      return;
    }
    this.challengeEditDialog = this.dialogService.open(DialogChallengeFormComponent, {
      header: '', // this.translate.instant('EDITPOST') + ' ' + post.title,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        Challenge: Challenge,
        users: this.challengesColumnsService.users,
      },
    });

    this.challengeEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'Challenge updated'});
        this.getChallenges();
      }
    });
  }

  challengeDetails(Challenge: Challenge): void {
    if (!this.navigation.getParamValue('Challenges.detailsInDialog', true)) {
      this.router.navigate(['/Challenges/details/' + Challenge.id]);
      return;
    }
    this.challengeDetailsDialog = this.dialogService.open(DialogChallengeDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        Challenge: Challenge,
      }
    });
  }

  challengeDelete(Challenge: Challenge): void {
    this.challengeToDelete = Challenge;
    this.messageService.clear();
    this.messageService.add({
      key: 'challengeDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + Challenge.title + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.challengesDataService.deleteChallenge(this.challengeToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Challenge deleted'});
      this.getChallenges();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Challenge not deleted'});
    });
  }

  onDeleteReject(): void {
    this.challengeToDelete = {};
    this.messageService.clear('ChallengeDelete');
  }

}
